import { useEffect, useRef, useCallback } from 'react';
import { SCANNER_CONSTANTS } from '../constants/scanner';
import { playAudioFeedback } from '../utils/audioFeedback';
import { useCharacterMap } from './useCharacterMap';

// Inputs that hold a choice, not typed text.
const NON_TEXT_INPUT_TYPES = new Set(['button', 'checkbox', 'color', 'file', 'image', 'radio', 'range', 'reset', 'submit']);

/**
 * Whether a keystroke on this element is someone typing into it. Anything else is left to the
 * scanner: a focused radio (a printer just picked), checkbox or read-only input (antd Select) would
 * otherwise swallow the next scan, so the scan works without clicking somewhere first.
 */
const isTextEntry = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return !target.readOnly;
  if (target instanceof HTMLInputElement) return !target.readOnly && !NON_TEXT_INPUT_TYPES.has(target.type);
  return false;
};

/**
 * Hook for handling keyboard-based barcode scanner input (HID scanners)
 *
 * HID barcode scanners act as keyboard input devices, typing the barcode
 * and pressing Enter. This hook accumulates keystrokes and detects when
 * a complete barcode has been scanned.
 *
 * Character Map Integration:
 * Automatically applies character map translation if configured, allowing
 * scanners with non-standard keyboard layouts to be used correctly.
 *
 * @param onScan - Callback function when a barcode is scanned (translatedCode, rawCode)
 * @param timeout - Milliseconds of inactivity before clearing buffer (default from SCANNER_CONSTANTS)
 *
 * @example
 * ```tsx
 * const handleScan = useCallback((code: string, rawCode?: string) => {
 *   console.log('Scanned:', code, 'Raw:', rawCode);
 * }, []);
 *
 * useKeyboardScanner(handleScan);
 * ```
 */
export const useKeyboardScanner = (
  onScan: (code: string, rawCode?: string) => void | Promise<void>,
  timeout: number = SCANNER_CONSTANTS.KEYBOARD_TIMEOUT_MS
) => {
  const bufferRef = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { translate } = useCharacterMap();

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ignore if user is typing in an input field
    if (isTextEntry(e.target)) {
      return;
    }

    // Shortcuts (copy, find, zoom) are no scan. AltGr arrives as Ctrl+Alt and types "@" and "#".
    if (e.metaKey || (e.ctrlKey && !e.altKey)) {
      return;
    }

    if (e.key.length === 1) {
      // A scan's keys must not act on the focused control: its space would press the button just
      // clicked (printing the previous label again) and letters pick options in a <select>. A space
      // on its own is still someone pressing the focused button.
      if (bufferRef.current !== '' || e.key !== ' ') {
        e.preventDefault();
      }

      // Regular character - add to buffer
      bufferRef.current += e.key;

      // Reset timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Auto-clear buffer after inactivity
      timeoutRef.current = setTimeout(() => {
        bufferRef.current = '';
      }, timeout);
    } else if (e.key === 'Enter') {
      // Enter key - process the scanned code
      const rawCode = bufferRef.current.trim();
      if (rawCode) {
        // The Enter ending a scan is not a press of the focused button either.
        e.preventDefault();
        // Apply character map translation if configured
        const translatedCode = translate(rawCode);
        // DEBUG: Log raw and translated codes
        console.log('[KeyboardScanner] Raw:', JSON.stringify(rawCode), '| Translated:', JSON.stringify(translatedCode));
        onScan(translatedCode, rawCode);
      }
      bufferRef.current = '';

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [onScan, timeout, translate]);

  // Attach keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleKeyDown]);
};
